import * as React from "react"
import { useEffect, useState, useCallback, useRef } from "react"
import { useNavigate } from "react-router-dom"
import { motion, AnimatePresence } from "motion/react"
import {
  Zap,
  Handshake,
  Plus,
  X,
  Users,
  UserCircle,
  ChevronDown,
  Check,
  Loader2,
  Lock,
  AlertCircle,
} from "lucide-react"
import { DotLottieReact } from "@lottiefiles/dotlottie-react"
import { useAuth, type AgentInfo, type DealSlotReward } from "@/contexts/AuthContext"
import { DealRewardSlotStep } from "@/components/dashboard/DealRewardSlotStep"
import goldCoinLottie from "@/public/lotties/gold-coin.lottie"

interface DealFeedItem {
  displayName: string
  pointsEarned: number
  createdAt: string
}

/* ── Agent selector (same as DashboardTab's) ────────────────────────── */

function ModalAgentSelector({
  agents,
  selectedAgent,
  onSelect,
  isLoading,
}: {
  agents: AgentInfo[]
  selectedAgent: AgentInfo | null
  onSelect: (agent: AgentInfo) => void
  isLoading: boolean
}) {
  const [isOpen, setIsOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setIsOpen(false)
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  if (isLoading) {
    return (
      <div className="flex items-center gap-3 px-4 py-3.5 bg-[#1A1A30] border-2 border-[#2A2A45] rounded-xl">
        <Loader2 className="w-4 h-4 animate-spin text-[#6A6A8A]" />
        <span className="text-[#6A6A8A] text-sm font-medium">Loading agents...</span>
      </div>
    )
  }

  return (
    <div ref={ref} className="relative">
      <motion.button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full flex items-center justify-between gap-3 px-4 py-3.5 bg-[#1C1C32] border-2 border-[#2A2A45] rounded-xl text-left transition-all duration-200 cursor-pointer ${
          isOpen ? "shadow-[2px_2px_0_0_#9B59B6] border-[#9B59B6]" : ""
        }`}
        whileTap={{ scale: 0.99 }}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center border-2 ${
            selectedAgent ? "bg-[#9B59B6]/10 border-[#9B59B6]" : "bg-[#1A1A30] border-[#2A2A45]"
          }`}>
            <UserCircle className={`w-5 h-5 ${selectedAgent ? "text-[#9B59B6]" : "text-[#6A6A8A]"}`} />
          </div>
          <div className="min-w-0">
            {selectedAgent ? (
              <>
                <p className="text-white text-sm font-bold truncate">{selectedAgent.displayName}</p>
                <p className="text-[#6A6A8A] text-xs">{selectedAgent.totalDeals} deals submitted</p>
              </>
            ) : (
              <p className="text-[#6A6A8A] text-sm font-medium">Select an agent</p>
            )}
          </div>
        </div>
        <motion.div animate={{ rotate: isOpen ? 180 : 0 }} transition={{ duration: 0.2 }}>
          <ChevronDown className="w-5 h-5 text-[#6A6A8A]" />
        </motion.div>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 w-full mt-2 bg-[#1C1C32] border-2 border-[#2A2A45] rounded-xl overflow-hidden"
          >
            <div className="max-h-48 overflow-y-auto">
              {agents.map((agent, i) => (
                <motion.button
                  key={agent._id}
                  type="button"
                  onClick={() => { onSelect(agent); setIsOpen(false) }}
                  className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors cursor-pointer ${
                    selectedAgent?._id === agent._id ? "bg-[#9B59B6]/10" : "hover:bg-[#1A1A30]"
                  }`}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.03 }}
                >
                  <div className="w-8 h-8 rounded-lg bg-[#9B59B6]/10 border-2 border-[#9B59B6]/30 flex items-center justify-center text-[#9B59B6] text-xs font-bold">
                    {agent.displayName.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-semibold truncate">{agent.displayName}</p>
                    <p className="text-[#6A6A8A] text-xs">{agent.totalDeals} deals</p>
                  </div>
                  {selectedAgent?._id === agent._id && <Check className="w-5 h-5 text-[#9B59B6]" />}
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

/* ── Toast ───────────────────────────────────────────────────────────── */

function Toast({ message, type, onClose }: { message: string; type: "success" | "error"; onClose: () => void }) {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000)
    return () => clearTimeout(timer)
  }, [onClose])

  const isSuccess = type === "success"
  return (
    <motion.div
      initial={{ opacity: 0, y: 50, scale: 0.9 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 50, scale: 0.9 }}
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60]"
    >
      <div className={`flex items-center gap-3 px-6 py-4 border-2 border-[#2A2A45] rounded-xl ${isSuccess ? "bg-[#32CD32]" : "bg-red-500"}`}>
        {isSuccess ? (
          <div className="w-6 h-6 shrink-0">
            <DotLottieReact src={goldCoinLottie} loop autoplay style={{ width: "100%", height: "100%" }} />
          </div>
        ) : (
          <AlertCircle className="w-5 h-5 text-white" />
        )}
        <span className="font-bold text-white">{message}</span>
      </div>
    </motion.div>
  )
}

/* ── Main DealMarquee ────────────────────────────────────────────────── */

export function DealMarquee() {
  const { user, getRecentDealFeed, submitDeal, getAgencyAgents } = useAuth()
  const navigate = useNavigate()
  const [deals, setDeals] = useState<DealFeedItem[]>([])
  const [isPaused, setIsPaused] = useState(false)

  // Deal modal state
  const [showDealModal, setShowDealModal] = useState(false)
  const [memberId, setMemberId] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selectedAgent, setSelectedAgent] = useState<AgentInfo | null>(null)
  const [agents, setAgents] = useState<AgentInfo[]>([])
  const [agentsLoading, setAgentsLoading] = useState(false)
  const [inputFocused, setInputFocused] = useState(false)
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null)
  const [rewardSlotDeal, setRewardSlotDeal] = useState<{
    dealId: string
    memberId: string
    recipientLabel?: string
  } | null>(null)
  const [isRewardSlotBusy, setIsRewardSlotBusy] = useState(false)

  const userType = user?.onboardingData?.userType
  const isAgency = userType === "agency"

  const fetchFeed = useCallback(async () => {
    const result = await getRecentDealFeed()
    if (result.success && result.deals) {
      setDeals(result.deals)
    }
  }, [getRecentDealFeed])

  useEffect(() => {
    fetchFeed()
    const interval = setInterval(fetchFeed, 60_000)
    return () => clearInterval(interval)
  }, [fetchFeed])

  // Fetch agents when modal opens for agency users
  useEffect(() => {
    if (!showDealModal || !isAgency) return
    setAgentsLoading(true)
    getAgencyAgents().then((result) => {
      if (result.success && result.agents) setAgents(result.agents)
      setAgentsLoading(false)
    })
  }, [showDealModal, isAgency, getAgencyAgents])

  const hasNoAgents = isAgency && !agentsLoading && agents.length === 0
  const isShowingRewardSlot = rewardSlotDeal !== null

  const resetDealModal = useCallback(() => {
    setMemberId("")
    setSelectedAgent(null)
    setInputFocused(false)
    setRewardSlotDeal(null)
    setIsRewardSlotBusy(false)
  }, [])

  const handleCloseDealModal = useCallback(() => {
    if (isSubmitting || isRewardSlotBusy) return
    setShowDealModal(false)
    resetDealModal()
  }, [isSubmitting, isRewardSlotBusy, resetDealModal])

  const handleRewardSlotSettled = useCallback((reward: DealSlotReward) => {
    const bonusCashTotal = reward.type === "bonus"
      ? (typeof reward.totalCashAmount === "number" ? reward.totalCashAmount : reward.amount)
      : reward.amount
    if (reward.type === "points") {
      setToast({ message: `Slot reward: +${reward.amount} points!`, type: "success" })
      return
    }
    if (reward.type === "bonus") {
      setToast({ message: `Slot reward: $${bonusCashTotal} bonus added!`, type: "success" })
      return
    }
    if (reward.type === "item") {
      setToast({ message: `Slot reward: ${reward.label || "Oval merch"} won!`, type: "success" })
      return
    }
    if (reward.type === "free_spins") {
      setToast({ message: `Slot reward: ${reward.amount} free spin${reward.amount === 1 ? "" : "s"}!`, type: "success" })
      return
    }
    setToast({ message: "Deal submitted! No extra slot reward this spin.", type: "success" })
  }, [])

  const handleSubmitDeal = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!memberId.trim() || isSubmitting) return
    if (isAgency && !selectedAgent) return

    setIsSubmitting(true)
    const submittedMemberId = memberId.trim()
    const recipientLabel = isAgency ? (selectedAgent?.displayName || "your agent") : "you"
    const result = await submitDeal(submittedMemberId, isAgency ? selectedAgent?._id : undefined)
    if (result.success) {
      setMemberId("")
      setSelectedAgent(null)
      if (result.deal?._id) {
        setRewardSlotDeal({
          dealId: result.deal._id,
          memberId: result.deal.memberId || submittedMemberId.toUpperCase(),
          recipientLabel,
        })
      } else {
        setToast({ message: "Deal submitted successfully!", type: "success" })
        setShowDealModal(false)
        resetDealModal()
      }
      // Refresh the feed after a short delay
      setTimeout(fetchFeed, 2000)
    } else {
      setToast({ message: result.message || "Failed to submit deal", type: "error" })
    }
    setIsSubmitting(false)
  }

  const isFormValid = memberId.trim() && !isSubmitting && (!isAgency || selectedAgent)

  const handleCtaClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    setShowDealModal(true)
  }

  const items =
    deals.length > 0
      ? deals.map((d) => `${d.displayName} just closed a deal!`)
      : ["Be the first to close a deal today!"]

  // Triplicate for seamless loop
  const displayItems = [...items, ...items, ...items]

  return (
    <>
      <motion.div
        className="w-full overflow-hidden relative group/marquee"
        onMouseEnter={() => setIsPaused(true)}
        onMouseLeave={() => setIsPaused(false)}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ type: "spring", bounce: 0.3 }}
      >
        {/* Main bar */}
        <div className="relative bg-gradient-to-r from-[#1a1a1a] via-[#2d2d2d] to-[#1a1a1a] border-b-[3px] border-[#FFD700]">
          <div className="flex items-center h-9">

            {/* Left badge */}
            <div className="shrink-0 flex items-center gap-1.5 pl-3 pr-4 h-full bg-gradient-to-r from-[#F37020] to-[#FF8C42] relative z-10">
              <div className="w-5 h-5 shrink-0 drop-shadow-md">
                <DotLottieReact src={goldCoinLottie} loop autoplay style={{ width: "100%", height: "100%" }} />
              </div>
              <span
                className="text-white text-[13px] tracking-[0.15em] whitespace-nowrap drop-shadow-sm"
                style={{ fontFamily: "var(--font-display)" }}
              >
                LIVE DEALS
              </span>
              {/* Angled edge */}
              <div className="absolute -right-3 top-0 bottom-0 w-6 bg-gradient-to-r from-[#FF8C42] to-transparent skew-x-[-12deg] origin-top-left" />
            </div>

            {/* Scrolling content — clicking navigates to leaderboard */}
            <div
              className="flex-1 overflow-hidden relative ml-1 cursor-pointer"
              onClick={() => navigate("/dashboard/leaderboard")}
            >
              {/* Left fade */}
              <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-[#2d2d2d] to-transparent z-10 pointer-events-none" />
              {/* Right fade */}
              <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-[#1a1a1a] to-transparent z-10 pointer-events-none" />

              <div
                className="flex items-center gap-6 whitespace-nowrap"
                style={{
                  animation: `marquee-scroll ${Math.max(4, items.length * 1)}s linear infinite`,
                  animationPlayState: isPaused ? "paused" : "running",
                }}
              >
                {displayItems.map((text, i) => (
                  <span key={i} className="flex items-center gap-2">
                    <Zap className="w-3.5 h-3.5 text-[#FFD700] fill-[#FFD700] shrink-0" />
                    <span
                      className="text-[#FFD700] text-[13px] tracking-wide"
                      style={{ fontFamily: "var(--font-display)" }}
                    >
                      {text}
                    </span>
                    <span className="text-[#F37020] text-xs mx-2">|</span>
                  </span>
                ))}
              </div>
            </div>

            {/* Right CTA — Submit Deal */}
            <div className="shrink-0 h-full flex items-center px-3 relative z-10">
              <motion.button
                onClick={handleCtaClick}
                className="flex items-center gap-1.5 px-2.5 py-1 bg-[#FFD700] border-2 border-[#2A2A45] rounded-lg  cursor-pointer"
                whileHover={{ scale: 1.05, boxShadow: "0 2px 10px rgba(243, 112, 32, 0.1)" }}
                whileTap={{ scale: 0.95, x: 1, y: 1 }}
              >
                <Handshake className="w-3 h-3 text-black" />
                <span
                  className="text-black text-[10px] font-black uppercase tracking-wider whitespace-nowrap"
                  style={{ fontFamily: "var(--font-body)" }}
                >
                  Submit Deal
                </span>
              </motion.button>
            </div>
          </div>

          {/* Bottom glow line */}
          <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#FFD700]/40 to-transparent" />
        </div>

        {/* Inline keyframes */}
        <style>{`
          @keyframes marquee-scroll {
            0% { transform: translateX(0); }
            100% { transform: translateX(-33.333%); }
          }
        `}</style>
      </motion.div>

      {/* Deal Submit Modal */}
      <AnimatePresence>
        {showDealModal && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Backdrop */}
            <motion.div
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={handleCloseDealModal}
            />

            {/* Modal Card */}
            <motion.div
              className={`relative bg-[#1C1C32] border-2 border-[#2A2A45] rounded-2xl w-full overflow-hidden ${
                isShowingRewardSlot ? "max-w-xl max-h-[92vh] flex flex-col" : "max-w-md"
              }`}
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ type: "spring", bounce: 0.3 }}
            >
              {/* Modal Header */}
              <div className="bg-gradient-to-r from-[#32CD32] via-[#2DBE2D] to-[#28A828] p-5 relative overflow-hidden">
                <motion.div
                  className="absolute inset-0 opacity-20 pointer-events-none"
                  style={{
                    backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 10px, white 10px, white 20px)",
                  }}
                  animate={{ x: [0, 40] }}
                  transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                />
                <motion.button
                  type="button"
                  onClick={handleCloseDealModal}
                  className="absolute top-3 right-3 z-20 w-8 h-8 rounded-full bg-black/20 flex items-center justify-center text-white hover:bg-black/40 transition-colors cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </motion.button>
                <div className="relative pr-12 flex items-center gap-3">
                  <motion.div
                    className="w-12 h-12 rounded-xl bg-[#1C1C32]/20 flex items-center justify-center"
                    animate={{ rotate: [0, 5, -5, 0] }}
                    transition={{ duration: 3, repeat: Infinity }}
                  >
                    <Plus className="w-6 h-6 text-white" />
                  </motion.div>
                  <div>
                    <h3 className="text-xl font-black text-white text-shadow-retro-sm">
                      {isShowingRewardSlot ? "Spin Deal Reward" : "Submit New Deal"}
                    </h3>
                    <p className="text-white/80 text-sm font-medium">
                      {isShowingRewardSlot
                        ? "Submit -> spin -> reveal, all in the same popup"
                        : isAgency ? "Submit on behalf of your agents" : "Earn 10 points per deal"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Modal Body */}
              <div className={isShowingRewardSlot ? "p-6 min-h-0 overflow-y-auto" : "p-6"}>
                <AnimatePresence mode="wait">
                  {isShowingRewardSlot && rewardSlotDeal ? (
                    <motion.div
                      key="reward-slot"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                    >
                      <DealRewardSlotStep
                        key={rewardSlotDeal.dealId}
                        dealId={rewardSlotDeal.dealId}
                        memberId={rewardSlotDeal.memberId}
                        recipientLabel={rewardSlotDeal.recipientLabel}
                        onBusyChange={setIsRewardSlotBusy}
                        onRewardSettled={handleRewardSlotSettled}
                        onSubmitAnother={() => {
                          setRewardSlotDeal(null)
                          setIsRewardSlotBusy(false)
                        }}
                        onClose={handleCloseDealModal}
                      />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="deal-form"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                    >
                      {/* No agents warning */}
                      {hasNoAgents && (
                        <motion.div
                          initial={{ opacity: 0, scale: 0.95 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 border-3 border-amber-400 shadow-[3px_3px_0_0_#d97706] mb-5"
                        >
                          <Lock className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                          <div>
                            <p className="text-amber-800 text-sm font-bold">No agents available</p>
                            <p className="text-amber-600 text-xs mt-1">
                              You need agents in your agency to submit deals. Share your Agency ID so agents can join.
                            </p>
                          </div>
                        </motion.div>
                      )}

                      <form onSubmit={handleSubmitDeal} className="space-y-4">
                        {/* Agent selector for agency users */}
                        {isAgency && !hasNoAgents && (
                          <div className="space-y-2">
                            <label className="block text-sm font-bold text-[#E8E4DE] flex items-center gap-1.5">
                              <Users className="w-4 h-4 text-[#9B59B6]" />
                              Select Agent
                            </label>
                            <ModalAgentSelector
                              agents={agents}
                              selectedAgent={selectedAgent}
                              onSelect={setSelectedAgent}
                              isLoading={agentsLoading}
                            />
                          </div>
                        )}

                        {/* Member ID Input */}
                        <div className="space-y-2">
                          <label htmlFor="marquee-memberId" className="block text-sm font-bold text-[#E8E4DE]">Member ID</label>
                          <motion.div
                            className="relative"
                            animate={{ scale: inputFocused ? 1.01 : 1 }}
                            transition={{ type: "spring", stiffness: 400, damping: 30 }}
                          >
                            <input
                              type="text"
                              id="marquee-memberId"
                              value={memberId}
                              onChange={(e) => setMemberId(e.target.value)}
                              onFocus={() => setInputFocused(true)}
                              onBlur={() => setInputFocused(false)}
                              placeholder="Enter member ID..."
                              disabled={isSubmitting || hasNoAgents}
                              className={`w-full px-4 py-3.5 bg-[#1A1A30] border-2 border-[#2A2A45] rounded-xl font-medium text-white placeholder:text-[#6A6A8A] focus:outline-none transition-all duration-200 disabled:opacity-40 ${
                                inputFocused ? "shadow-[3px_3px_0_0_#32CD32] border-[#32CD32] bg-[#1C1C32]" : ""
                              }`}
                            />
                          </motion.div>
                        </div>

                        {/* Submit Button */}
                        <motion.button
                          type="submit"
                          disabled={!isFormValid || hasNoAgents}
                          className={`w-full flex items-center justify-center gap-2.5 px-5 py-4 border-2 border-[#2A2A45] rounded-xl font-bold text-lg transition-all cursor-pointer ${
                            isFormValid
                              ? "bg-[#32CD32] text-white"
                              : "bg-[#222240] text-[#6A6A8A] cursor-not-allowed"
                          }`}
                          whileHover={isFormValid ? { scale: 1.02, boxShadow: "0 4px 20px rgba(243, 112, 32, 0.2)" } : {}}
                          whileTap={isFormValid ? { scale: 0.98, boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)", x: 2, y: 2 } : {}}
                        >
                          {isSubmitting ? (
                            <>
                              <Loader2 className="w-5 h-5 animate-spin" />
                              Submitting...
                            </>
                          ) : (
                            <>
                              <Handshake className="w-5 h-5" />
                              {isAgency
                                ? selectedAgent ? `Submit for ${selectedAgent.displayName}` : "Select an agent first"
                                : "Submit Deal"
                              }
                            </>
                          )}
                        </motion.button>
                      </form>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toast */}
      <AnimatePresence>
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      </AnimatePresence>
    </>
  )
}
